import { TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from "@mui/lab";
import { Alert, Box, Stack, Typography } from "@mui/material";
import UserAvatar from "@entrilia/shared/components/UserAvatar";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { MemberChangeContributor } from "../../../../../api/types/workflowTypes";
import ChangeRequestComment from "../ChangeRequestComment";
import ContributionAttachmentRow from "./ContributionAttachmentRow";
import { getRestrictedEvidenceMessage } from "./membersChangeRequestHelper";

interface Props {
  workflowId: string;
  contributor: MemberChangeContributor;
  isLast?: boolean;
}

const ContributionTimelineItem = ({ workflowId, contributor, isLast }: Props) => {
  const restrictedEvidenceMessage = getRestrictedEvidenceMessage(contributor);
  const pendingFundScope = contributor.pendingFunds.map((fund) => fund.fundName).join(" · ");

  return (
    <TimelineItem>
      <TimelineSeparator>
        <TimelineDot sx={{ m: 0, p: 0, border: "none", boxShadow: "none", bgcolor: "transparent" }}>
          <UserAvatar size="small" userName={contributor.contributorName} />
        </TimelineDot>
        {!isLast && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent sx={{ pt: 0, pb: isLast ? 0 : 2 }}>
        <Stack spacing={1}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
              <Typography variant="subtitle2">{contributor.contributorName}</Typography>
              <Typography color="textSecondary">
                {contributor.withdrewChanges ? "withdrew changes from" : "contributed to"}
              </Typography>
              <Typography>this Change Request</Typography>
            </Stack>
            <Typography noWrap color="textSecondary" sx={{ flexShrink: 0 }}>
              {convertISODateTime(contributor.latestContributionAtUtc)}
            </Typography>
          </Box>
          {pendingFundScope && (
            <Typography color="textSecondary">
              {contributor.withdrewChanges ? "Withdrawn fund scope:" : "Pending fund scope:"}{" "}
              <Typography component="span" color="textPrimary">
                {pendingFundScope}
              </Typography>
            </Typography>
          )}
          {contributor.comment && <ChangeRequestComment comment={contributor.comment} />}
          {contributor.attachments.length > 0 && (
            <Stack spacing={1}>
              {contributor.attachments.map((attachment) => (
                <ContributionAttachmentRow
                  key={attachment.dataCatalogId}
                  workflowId={workflowId}
                  attachment={attachment}
                />
              ))}
            </Stack>
          )}
          {/* Last, so it annotates the evidence above it: the trail can be partly visible, and this says the rest is not. */}
          {restrictedEvidenceMessage && <Alert severity="warning">{restrictedEvidenceMessage}</Alert>}
        </Stack>
      </TimelineContent>
    </TimelineItem>
  );
};

export default ContributionTimelineItem;
